import { useMutation } from 'react-query';
import { AxiosError } from 'axios';
import { Axios } from '@utils/api/axios-instance';
import { triggerDownloadFromUrl } from '@utils/api/documents/use-get-document-direct';

/** Formats supported by the back-office export endpoint (#2020). */
export type SessionExportFormat = 'srt' | 'vtt' | 'docx' | 'txt';

export interface ExportSessionDocumentParams {
  zoomId: string;
  documentId: number;
  format: SessionExportFormat;
}

/**
 * Download a session's document in the requested format via the sysop-scoped
 * admin export endpoint (#2020).
 *
 * The endpoint reuses the same conversion controllers as the customer v3 GET
 * document endpoint, so the bytes are identical to a customer-side export.
 * We fetch as a Blob (responseType: 'blob') so the browser doesn't try to
 * parse DOCX/SRT/etc. as JSON, then hand off to `triggerDownloadFromUrl`
 * (the shared helper already used by the customer download flow) for the
 * actual save-to-disk via a programmatic `<a download>` click.
 *
 * Filename is taken from the response's Content-Disposition header so the
 * back-office matches what the backend computed from the session filename.
 */
async function exportSessionDocument({
  zoomId,
  documentId,
  format
}: ExportSessionDocumentParams): Promise<void> {
  const response = await Axios.get<Blob>(
    `/api/admin/session-inspector/${zoomId}/documents/${documentId}/export`,
    {
      params: { format },
      responseType: 'blob'
    }
  );

  const contentType =
    (response.headers['content-type'] as string | undefined) ??
    'application/octet-stream';

  // Pull filename from Content-Disposition (the backend sets both filename=""
  // and filename*=UTF-8''… per RFC 5987). Fallback to a sensible default if
  // the header isn't present.
  const disposition =
    (response.headers['content-disposition'] as string | undefined) ?? '';
  const filename =
    parseFilenameFromDisposition(disposition) || `${zoomId}.${format}`;

  const objectUrl = window.URL.createObjectURL(
    new Blob([response.data], { type: contentType })
  );
  try {
    await triggerDownloadFromUrl(objectUrl, filename, contentType);
  } finally {
    window.URL.revokeObjectURL(objectUrl);
  }
}

function parseFilenameFromDisposition(disposition: string): string | null {
  // Prefer the RFC 5987 form (filename*=UTF-8''…) when present — it's
  // unambiguous for non-ASCII characters. Fall back to the plain filename="…"
  // form when only that is set.
  const utf8Match = disposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim());
    } catch {
      /* fall through */
    }
  }
  const asciiMatch = disposition.match(/filename\s*=\s*"?([^";]+)"?/i);
  if (asciiMatch?.[1]) {
    return asciiMatch[1].trim();
  }
  return null;
}

export const useExportSessionDocument = () => {
  return useMutation<void, AxiosError, ExportSessionDocumentParams>(
    exportSessionDocument
  );
};
